import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Search, Edit2 } from 'lucide-react';

export default function AdminRoleManagement() {
  const [email, setEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  type CapabilityKey = 'can_add_movies' | 'can_edit_movies' | 'can_delete_movies' | 'can_edit_screenings' | 'can_cancel_bookings';
  type Caps = Partial<Record<CapabilityKey, boolean>>;
  type ProfileRow = { id: string; email: string; role: string | null; capabilities?: Caps; validatorOrganizer?: { id: number; name: string } | null };
  const [users, setUsers] = useState<ProfileRow[]>([]);
  const [usersCount, setUsersCount] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'editor' | 'organizer' | 'validator' | 'guest'>('all');

  const [panelUser, setPanelUser] = useState<ProfileRow | null>(null);
  const [roleDraft, setRoleDraft] = useState<Record<string, 'admin' | 'editor' | 'organizer' | 'validator' | 'guest'>>({});
  const [capsDraft, setCapsDraft] = useState<Record<string, Caps>>({});
  const [saving, setSaving] = useState(false);
  const [organizers, setOrganizers] = useState<{ id: number; name: string }[]>([]);
  const [selectedOrgForUser, setSelectedOrgForUser] = useState<Record<string, number | null>>({});
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewValidators, setOverviewValidators] = useState<{ user_id: string; email: string }[]>([]);
  const [overviewEvents, setOverviewEvents] = useState<{ id: number; title: string; start_time: string; location: string }[]>([]);
  const [commissionDraft, setCommissionDraft] = useState<string>('13');
  const [phoneBookingDraft, setPhoneBookingDraft] = useState<Record<string, boolean>>({});


  const makeOrganizer = async (enable: boolean, targetEmail?: string) => {
    const e = targetEmail ?? email;
    if (!e) {
      toast.error('Enter an email');
      return;
    }
    try {
      const { error } = await supabase.rpc('assign_organizer_by_email', { target_email: e, enable });
      if (error) {
        toast.error('Failed to update organizer');
      } else {
        toast.success(enable ? 'Organizer assigned' : 'Organizer removed');
      }
    } catch {
      toast.error('Failed to update organizer');
    }
  };

  const makeEditor = async (enable: boolean, targetEmail?: string) => {
    const e = targetEmail ?? email;
    if (!e) {
      toast.error('Enter an email');
      return;
    }
    try {
      const { error } = await supabase.rpc('assign_editor_by_email', { target_email: e, enable });
      if (error) {
        toast.error('Failed to update editor');
      } else {
        toast.success(enable ? 'Editor assigned' : 'Editor removed');
      }
    } catch {
      toast.error('Failed to update editor');
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const from = page * pageSize;
      const to = from + pageSize - 1;
      let q = supabase
        .from('profiles')
        .select('id,email,role', { count: 'exact' })
        .order('email', { ascending: true })
        .range(from, to);
      if (searchTerm.trim()) q = q.ilike('email', `%${searchTerm.trim()}%`);
      if (roleFilter !== 'all') {
        if (roleFilter === 'guest') {
          q = q.eq('role', 'guest');
        } else {
          q = q.eq('role', roleFilter);
        }
      }
      const { data, count, error } = await q;
      const attachValidatorOrganizer = async (rows: ProfileRow[]) => {
        const ids = rows.filter(r => r.role === 'validator').map(r => r.id);
        if (ids.length === 0) return rows;
        type MapRow = { user_id: string; organizer_id: number; organizers: { id: number; name: string } | { id: number; name: string }[] | null };
        const { data: mapRows } = await supabase
          .from('organizer_validators')
          .select('user_id,organizer_id,organizers(id,name)')
          .in('user_id', ids);
        const m = new Map<string, { id: number; name: string }>();
        ((mapRows || []) as MapRow[]).forEach(r => {
          const org = Array.isArray(r.organizers) ? r.organizers[0] : r.organizers;
          if (org) m.set(r.user_id, { id: org.id, name: org.name });
        });
        return rows.map(r => r.role === 'validator' ? { ...r, validatorOrganizer: m.get(r.id) || null } : r);
      };
      if (error) {
        const term = searchTerm.trim();
        const { data: fallback } = await supabase.rpc('search_users_for_organizer', { term, limit_count: pageSize });
        const rows = (fallback || []) as { user_id: string; email: string; role: string }[];
        const base = rows.map(r => ({ id: r.user_id, email: r.email, role: (r.role && r.role.length ? r.role : 'guest'), capabilities: {} as Caps, validatorOrganizer: null }));
        const filtered = roleFilter === 'all' ? base : roleFilter === 'guest' ? base.filter(r => r.role === 'guest') : base.filter(r => r.role === roleFilter);
        const enriched = await attachValidatorOrganizer(filtered);
        setUsers(enriched);
        setUsersCount(filtered.length);
      } else if (data && data.length > 0) {
        const base = ((data || []) as { id: string; email: string; role: string | null }[]).map(r => ({ id: r.id, email: r.email, role: (r.role || 'guest'), capabilities: {}, validatorOrganizer: null }));
        const enriched = await attachValidatorOrganizer(base);
        setUsers(enriched);
        setUsersCount(count || 0);
      } else {
        const term = searchTerm.trim();
        const { data: fallback } = await supabase.rpc('search_users_for_organizer', { term, limit_count: pageSize });
        const rows = (fallback || []) as { user_id: string; email: string; role: string }[];
        const mapped = rows.map(r => ({ id: r.user_id, email: r.email, role: (r.role && r.role.length ? r.role : 'guest'), capabilities: {} as Caps, validatorOrganizer: null }));
        const filtered = roleFilter === 'all' ? mapped : roleFilter === 'guest' ? mapped.filter(r => r.role === 'guest') : mapped.filter(r => r.role === roleFilter);
        const enriched = await attachValidatorOrganizer(filtered);
        setUsers(enriched);
        setUsersCount(filtered.length);
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [page, searchTerm, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    async function loadOrgs() {
      try {
        const { data, error } = await supabase.rpc('list_current_organizers');
        if (!error && data) {
          setOrganizers(((data as any[]) || []).map(d => ({ id: Number(d.id), name: String(d.name) })));
          return;
        }
      } catch { }
      const { data: allOrgs } = await supabase.from('organizers').select('id,name,owner_id');
      const orgs = (allOrgs || []) as { id: number; name: string; owner_id: string | null }[];
      if (!orgs.length) { setOrganizers([]); return; }
      const ownerIds = orgs.map(o => o.owner_id).filter(Boolean) as string[];
      if (!ownerIds.length) { setOrganizers([]); return; }
      const { data: profiles, error: pErr } = await supabase.from('profiles').select('id, role').in('id', ownerIds);
      if (pErr || !profiles) { setOrganizers([]); return; }
      const allowed = new Set(((profiles || []) as { id: string; role: string }[]).filter(p => p.role === 'organizer').map(p => p.id));
      setOrganizers(orgs.filter(o => o.owner_id && allowed.has(o.owner_id)).map(o => ({ id: o.id, name: o.name })));
    }
    loadOrgs();
  }, []);

  useEffect(() => {
    const pu = panelUser;
    if (!pu) {
      setOverviewValidators([]);
      setOverviewEvents([]);
      return;
    }
    const currentRole = roleDraft[pu.id] || (pu.role || 'guest');
    if (currentRole !== 'organizer') {
      setOverviewValidators([]);
      setOverviewEvents([]);
      return;
    }
    setOverviewLoading(true);
    (async () => {
      const { data: orgRow } = await supabase.from('organizers').select('id, commission_percent, can_phone_book').eq('owner_id', pu.id).maybeSingle();
      const orgId = (orgRow as { id: number; commission_percent?: number; can_phone_book?: boolean } | null)?.id || null;
      if (orgRow?.commission_percent !== undefined) {
        setCommissionDraft(String(orgRow.commission_percent));
      } else {
        setCommissionDraft('13');
      }
      if (orgRow?.can_phone_book !== undefined) {
        setPhoneBookingDraft(d => ({ ...d, [pu.id]: orgRow.can_phone_book }));
      } else {
        setPhoneBookingDraft(d => ({ ...d, [pu.id]: true }));
      }
      if (!orgId) {
        setOverviewValidators([]);
        setOverviewEvents([]);
        setOverviewLoading(false);
        return;
      }
      const { data: ovMap } = await supabase.from('organizer_validators').select('user_id').eq('organizer_id', orgId);
      const vIds = ((ovMap || []) as { user_id: string }[]).map(r => r.user_id);
      let validators: { user_id: string; email: string }[] = [];
      if (vIds.length) {
        const { data: vProfiles } = await supabase.from('profiles').select('id,email').in('id', vIds);
        validators = ((vProfiles || []) as { id: string; email: string }[]).map(p => ({ user_id: p.id, email: p.email }));
      }
      const { data: orgMovies } = await supabase.from('movies').select('id,title').eq('organizer_id', orgId);
      const mIds = ((orgMovies || []) as { id: number; title: string }[]).map(m => m.id);
      let events: { id: number; title: string; start_time: string; location: string }[] = [];
      if (mIds.length) {
        const { data: screenings } = await supabase.from('screenings').select('id,start_time,location,movie_id').in('movie_id', mIds).order('start_time', { ascending: false });
        const titleMap = new Map<number, string>(((orgMovies || []) as { id: number; title: string }[]).map(m => [m.id, m.title]));
        events = ((screenings || []) as { id: number; start_time: string; location: string; movie_id: number }[]).map(s => ({ id: s.id, title: titleMap.get(s.movie_id) || '', start_time: s.start_time, location: s.location }));
      }
      setOverviewValidators(validators);
      setOverviewEvents(events);
      setOverviewLoading(false);
    })();
  }, [panelUser, roleDraft]);

  return (
    <div id="role-management" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="p-4 sm:p-6 border-b border-gray-700">
          <div className="flex w-full items-stretch rounded-lg h-11 max-w-sm">
            <div className="text-gray-500 dark:text-gray-400 flex bg-gray-200 dark:bg-gray-700 items-center justify-center pl-3.5 rounded-l-lg">
              <Search className="h-5 w-5" />
            </div>
            <input
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
              className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 dark:text-white focus:outline-0 focus:ring-2 focus:ring-indigo-600/50 border border-gray-300 dark:border-none bg-white dark:bg-gray-700 h-full placeholder:text-gray-500 dark:placeholder:text-gray-400 px-4 text-sm"
              placeholder="Search users by name or email..."
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => { setRoleFilter('all'); setPage(0); }} className={`px-3 h-8 rounded-full text-sm ${roleFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-300'}`}>All</button>
            <button onClick={() => { setRoleFilter('admin'); setPage(0); }} className={`px-3 h-8 rounded-full text-sm ${roleFilter === 'admin' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Administrator</button>
            <button onClick={() => { setRoleFilter('editor'); setPage(0); }} className={`px-3 h-8 rounded-full text-sm ${roleFilter === 'editor' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Editor</button>
            <button onClick={() => { setRoleFilter('organizer'); setPage(0); }} className={`px-3 h-8 rounded-full text-sm ${roleFilter === 'organizer' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Organizer</button>
            <button onClick={() => { setRoleFilter('validator'); setPage(0); }} className={`px-3 h-8 rounded-full text-sm ${roleFilter === 'validator' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Validator</button>
            <button onClick={() => { setRoleFilter('guest'); setPage(0); }} className={`px-3 h-8 rounded-full text-sm ${roleFilter === 'guest' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Guest</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-700">
              <tr>
                <th className="px-6 py-4 text-sm font-semibold text-gray-700 dark:text-gray-300">User</th>
                <th className="px-6 py-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Roles</th>
                <th className="px-6 py-4 text-sm font-semibold text-gray-700 dark:text-gray-300 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loadingUsers ? (
                <tr><td className="px-6 py-4 text-gray-700 dark:text-gray-400" colSpan={3}>Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td className="px-6 py-4 text-gray-700 dark:text-gray-400" colSpan={3}>No users found</td></tr>
              ) : (
                users.map(u => (
                  <tr key={u.id}>
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="rounded-full size-10 bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-white flex items-center justify-center">
                          {(u.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <p className="text-gray-900 dark:text-white text-sm font-medium">{u.email.split('@')[0]}</p>
                          <p className="text-gray-700 dark:text-gray-400 text-sm">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {u.role === 'admin' && (<span className="inline-flex items-center rounded-full bg-indigo-600/10 px-2.5 py-0.5 text-xs font-medium text-indigo-400">Administrator</span>)}
                        {u.role === 'editor' && (<span className="inline-flex items-center rounded-full bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-300">Editor</span>)}
                        {u.role === 'organizer' && (<span className="inline-flex items-center rounded-full bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-300">Organizer</span>)}
                        {u.role === 'guest' && (<span className="inline-flex items-center rounded-full bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-300">Guest</span>)}
                        {u.role === 'validator' && (
                          <span className="inline-flex items-center rounded-full bg-green-600/10 px-2.5 py-0.5 text-xs font-medium text-green-400">
                            Validator{u.validatorOrganizer?.name ? ` • ${u.validatorOrganizer.name}` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        className="flex ml-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg h-9 px-3 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white text-sm font-bold hover:dark:bg-gray-600"
                        onClick={() => {
                          setEmail(u.email);
                          setPanelUser(u);
                          setRoleDraft(d => ({ ...d, [u.id]: (u.role as 'admin' | 'editor' | 'organizer' | 'validator' | 'guest') || 'guest' }));
                          setCapsDraft(d => ({ ...d, [u.id]: { ...(u.capabilities || {}) } }));
                          const el = document.getElementById('role-permissions-panel');
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 sm:p-6 border-t border-gray-700 flex justify-between items-center">
          <p className="text-sm text-gray-400">Showing {users.length ? page * pageSize + 1 : 0} to {Math.min((page + 1) * pageSize, usersCount)} of {usersCount} users</p>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className="flex cursor-pointer items-center justify-center overflow-hidden rounded-lg size-9 bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">‹</button>
            <button disabled={(page + 1) * pageSize >= usersCount} onClick={() => setPage(p => p + 1)} className="flex cursor-pointer items-center justify-center overflow-hidden rounded-lg size-9 bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">›</button>
          </div>
        </div>
      </div>
      <div id="role-permissions-panel" className="lg:col-span-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 self-start lg:sticky lg:top-8">
        <div className="p-6">
          <h2 className="text-gray-900 dark:text-white text-lg font-semibold">{panelUser ? `Manage Permissions for ${panelUser.email.split('@')[0]}` : 'Select a user to manage'}</h2>
          <p className="text-gray-700 dark:text-gray-400 text-sm mt-1">{panelUser ? 'Select the permissions for the assigned roles.' : 'Click Manage for a user to edit permissions.'}</p>
        </div>
        {panelUser && (
          <>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-gray-900 dark:text-white text-base font-semibold">Role</h3>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => setRoleDraft(d => ({ ...d, [panelUser.id]: 'editor' }))} className={`px-3 h-8 rounded-full text-sm ${roleDraft[panelUser.id] === 'editor' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Editor</button>
                <button onClick={() => setRoleDraft(d => ({ ...d, [panelUser.id]: 'organizer' }))} className={`px-3 h-8 rounded-full text-sm ${roleDraft[panelUser.id] === 'organizer' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Organizer</button>
                <button onClick={() => setRoleDraft(d => ({ ...d, [panelUser.id]: 'validator' }))} className={`px-3 h-8 rounded-full text-sm ${roleDraft[panelUser.id] === 'validator' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Validator</button>
                <button onClick={() => setRoleDraft(d => ({ ...d, [panelUser.id]: 'guest' }))} className={`px-3 h-8 rounded-full text-sm ${roleDraft[panelUser.id] === 'guest' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Guest</button>
                <button disabled className={`px-3 h-8 rounded-full text-sm ${roleDraft[panelUser.id] === 'admin' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'} opacity-50 cursor-not-allowed`}>Admin</button>
              </div>
              {roleDraft[panelUser.id] === 'validator' && (
                <div className="mt-4">
                  <label className="block text-sm text-gray-300 mb-2">Organizer</label>
                  <select
                    value={selectedOrgForUser[panelUser.id] ?? ''}
                    onChange={(e) => setSelectedOrgForUser(d => ({ ...d, [panelUser.id]: e.target.value ? Number(e.target.value) : null }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  >
                    <option value="">Select organizer</option>
                    {organizers.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-gray-900 dark:text-white text-base font-semibold">Powers</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {(['can_add_movies', 'can_edit_movies', 'can_delete_movies', 'can_edit_screenings', 'can_cancel_bookings'] as CapabilityKey[]).map(key => (
                  <label key={key} className="flex items-center gap-3 text-gray-900 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={!!capsDraft[panelUser.id]?.[key]}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setCapsDraft(d => ({ ...d, [panelUser.id]: { ...(d[panelUser.id] || {}), [key]: checked } }));
                      }}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    />
                    <span className="text-sm">{key.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            {(roleDraft[panelUser.id] === 'organizer' || panelUser.role === 'organizer') && (
              <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-gray-900 dark:text-white text-base font-semibold">Commission Settings</h3>
                <div className="mt-4">
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Commission Percentage (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={commissionDraft}
                    onChange={(e) => setCommissionDraft(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white focus:outline-none focus:border-indigo-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Percentage deducted from ticket sales for platform fees. Inclusive pricing applies.
                  </p>
                </div>
                <div className="mt-4">
                  <label className="flex items-center gap-3 text-gray-900 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={phoneBookingDraft[panelUser.id] ?? true}
                      onChange={(e) => setPhoneBookingDraft(d => ({ ...d, [panelUser.id]: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    />
                    <span className="text-sm">Enable Phone Booking</span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Allow this organizer to make phone bookings on behalf of customers.
                  </p>
                </div>
              </div>
            )}
            {(roleDraft[panelUser.id] === 'organizer' || panelUser.role === 'organizer') && (
              <div className="p-6 border-t border-gray-700">
                <h3 className="text-white text-base font-semibold">Organizer Details</h3>
                {overviewLoading ? (
                  <p className="text-gray-400 mt-2">Loading…</p>
                ) : (
                  <div className="mt-4 space-y-6">
                    <div>
                      <p className="text-sm text-gray-300 mb-2">Validators</p>
                      {overviewValidators.length === 0 ? (
                        <p className="text-gray-400">No validators</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {overviewValidators.map(v => (
                            <span key={v.user_id} className="inline-flex items-center rounded-full bg-green-600/10 px-2.5 py-0.5 text-xs font-medium text-green-400">{v.email}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-300 mb-2">Events</p>
                      {overviewEvents.length === 0 ? (
                        <p className="text-gray-400">No events</p>
                      ) : (
                        <div className="space-y-2">
                          {overviewEvents.map(ev => {
                            const dt = new Date(ev.start_time);
                            const status = dt.getTime() >= Date.now() ? 'Valid' : 'Expired';
                            const statusClass = status === 'Valid' ? 'bg-indigo-600/10 text-indigo-400' : 'bg-red-600/10 text-red-400';
                            return (
                              <div key={ev.id} className="flex items-center justify-between bg-gray-900/60 border border-gray-700 rounded px-3 py-2">
                                <div className="flex flex-col">
                                  <span className="text-sm text-white">{ev.title}</span>
                                  <span className="text-xs text-gray-300">{dt.toLocaleString()} • {ev.location}</span>
                                </div>
                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass}`}>{status}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button onClick={() => { setPanelUser(null); }} className="flex cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white text-sm font-bold">Cancel</button>
              <button
                disabled={saving}
                onClick={async () => {
                  if (!panelUser) return;
                  setSaving(true);
                  const currentRole = (panelUser.role as 'admin' | 'editor' | 'organizer' | 'validator' | 'guest') || 'guest';
                  const targetRole = roleDraft[panelUser.id] || currentRole;

                  try {
                    // Logic to switch roles
                    if (targetRole === 'organizer') {
                      await makeOrganizer(true, panelUser.email);

                      // Update commission
                      const { data: orgRow } = await supabase.from('organizers').select('id').eq('owner_id', panelUser.id).maybeSingle();
                      if (orgRow) {
                        const val = parseFloat(commissionDraft);
                        if (!isNaN(val)) {
                          const { error: commError } = await supabase.rpc('admin_update_commission_percent', {
                            p_organizer_id: orgRow.id,
                            p_new_percent: val
                          });
                          if (commError) {
                            console.error('Failed to update commission:', commError);
                            toast.error('Failed to update commission percent');
                          }
                        }

                        // Update phone booking permission
                        const canPhoneBook = phoneBookingDraft[panelUser.id] ?? true;
                        const { error: phoneBookError } = await supabase.rpc('admin_update_phone_booking_permission', {
                          p_organizer_id: orgRow.id,
                          p_can_phone_book: canPhoneBook
                        });
                        if (phoneBookError) {
                          console.error('Failed to update phone booking permission:', phoneBookError);
                          toast.error('Failed to update phone booking permission');
                        }
                      }
                    } else if (targetRole === 'editor') {
                      await makeEditor(true, panelUser.email);
                    } else if (targetRole === 'validator') {
                      const orgId = selectedOrgForUser[panelUser.id];
                      if (!orgId) {
                        toast.error('Select an organizer for the validator');
                        setSaving(false);
                        return;
                      }
                      const { error } = await supabase.rpc('assign_validator_by_email', { target_email: panelUser.email, organizer_id: orgId, enable: true });
                      if (error) {
                        toast.error('Failed to assign validator');
                      } else {
                        toast.success('Validator assigned');
                      }
                    } else if (targetRole === 'guest') {
                      // Demote to guest
                      if (currentRole === 'organizer') {
                        await makeOrganizer(false, panelUser.email);
                      } else if (currentRole === 'validator' && panelUser.validatorOrganizer) {
                        // Attempt to remove validator status if we know the org. 
                        // If not, we can't easily remove specific entries via existing RPCs without org_id?
                        // Actually assign_validator_by_email with enable=false requires organizer_id too?
                        // Let's check assign_validator signature or usage.
                        // Assuming we might need to just set role to guest directly if we can't invoke specific demotion.
                        // But makeEditor(false) sets role to guest.
                        await makeEditor(false, panelUser.email);
                      } else {
                        // Default demotion
                        await makeEditor(false, panelUser.email);
                      }
                    } else {
                      // Admin or other
                      toast.error('Role assignment restricted');
                    }

                    // Update capabilities
                    const currentCaps = capsDraft[panelUser.id] || {};
                    const { error: capError } = await supabase.rpc('set_user_capabilities', { target_user_id: panelUser.id, caps: currentCaps });
                    if (capError) {
                      toast.error('Failed to update powers');
                    } else {
                      toast.success('Updated successfully');
                    }
                  } catch {
                    toast.error('Failed to update user');
                  } finally {
                    setSaving(false);
                    setPanelUser(null);
                    fetchUsers();
                  }
                }}
                className="flex cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-5 bg-indigo-600 text-white text-sm font-bold disabled:opacity-50"
              >
                Save Changes
              </button>
            </div>
          </>
        )}
      </div>
    </div >
  );
}
